import { useState } from "react";
import "@/App.css";
import { Phone, Mail, ChevronDown, ChevronUp, Menu, X, Briefcase, Home as HomeIcon, Globe, MessageSquare, Check, Clock, Zap, TrendingUp, Users, Star, Monitor, Search, Shield } from "lucide-react";

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);

  const checkout = (link) => {
    window.location.href = link;
  };

  const navigation = [
    { id: 'home', label: 'Home', icon: HomeIcon },
    { id: 'services', label: 'Services', icon: Briefcase },
    { id: 'contact', label: 'Contact', icon: MessageSquare },
  ];

  const faqItems = [
    {
      question: "How long does it take to build my website?",
      answer: "Most websites are completed within 48-72 hours. We prioritize fast turnaround so you can start getting clients immediately."
    },
    {
      question: "What's included in the $350 website package?",
      answer: "You get a fully custom, mobile-responsive website designed specifically for your business. This includes up to 5 pages, contact forms, Google Maps integration, and basic SEO setup."
    },
    {
      question: "What does the $25/month hosting plan include?",
      answer: "Our monthly plan includes reliable hosting, SSL security, ongoing SEO optimization, minor content updates, and priority support. Your website stays fast, secure, and optimized."
    },
    {
      question: "Do I own my website?",
      answer: "Absolutely! You own 100% of your website content and design. We simply help you build and maintain it."
    },
    {
      question: "Can I see examples of your work?",
      answer: "Yes! We're currently building our portfolio and offering discounted rates. Contact us to see samples relevant to your industry."
    },
    {
      question: "What if I need changes after the website is built?",
      answer: "Minor text and image updates are included with our monthly hosting plan. For larger redesigns or new features, we offer competitive rates."
    }
  ];

  const NavButton = ({ item }) => (
    <button
      data-testid={`nav-${item.id}`}
      onClick={() => {
        setCurrentPage(item.id);
        setMobileMenuOpen(false);
      }}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
        currentPage === item.id
          ? 'bg-[#0F172A] text-white'
          : 'text-slate-600 hover:text-[#FF4500] hover:bg-slate-100'
      }`}
    >
      <item.icon className="w-4 h-4" />
      {item.label}
    </button>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Offer Banner */}
      <div className="offer-banner text-white py-3 px-4 text-center" data-testid="offer-banner">
        <p className="text-sm md:text-base font-semibold">
          <span className="text-[#FF4500]">LIMITED TIME:</span> $350 Website Package — 
          <span className="text-[#FF4500] font-bold"> SAVE $100!</span>
          <span className="hidden sm:inline"> | Fast 48hr Delivery</span>
        </p>
      </div>

      {/* Header */}
      <header className="glass-header sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button 
              data-testid="logo"
              onClick={() => setCurrentPage('home')}
              className="flex items-center gap-2"
            >
              <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">E</span>
              </div>
              <span className="font-bold text-xl text-[#0F172A] tracking-tight">ETX Digital</span>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              {navigation.map((item) => (
                <NavButton key={item.id} item={item} />
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white py-4 px-6">
            <div className="flex flex-col gap-2">
              {navigation.map((item) => (
                <NavButton key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>
        {/* HOME PAGE */}
        {currentPage === 'home' && (
          <div data-testid="home-page" className="animate-fade-in-up">
            {/* Hero Section */}
            <section className="relative overflow-hidden">
              <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-8">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4500] mb-4">
                        Web Design Agency
                      </p>
                      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.1] text-[#0F172A]">
                        Websites That Get You <span className="text-[#FF4500]">Clients</span>
                      </h1>
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
                      We design high-converting websites for local businesses anywhere. Get more calls, more leads, and more paying customers.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <button
                        data-testid="hero-cta"
                        onClick={() => setCurrentPage('contact')}
                        className="btn-lift glow-pulse bg-[#FF4500] hover:bg-[#E03C00] text-white px-8 py-4 rounded-lg font-semibold text-lg"
                      >
                        Get Started — $350
                      </button>
                      <button
                        data-testid="hero-secondary-cta"
                        onClick={() => setCurrentPage('contact')}
                        className="border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
                      >
                        Contact Us
                      </button>
                    </div>
                  </div>
                  <div className="relative hidden lg:block">
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 shadow-2xl">
                      <img
                        src="https://images.unsplash.com/photo-1772899129194-15f58b98978f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxzdGFyayUyMHdoaXRlJTIwYWJzdHJhY3QlMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc2MjE0Nzk0fDA&ixlib=rb-4.1.0&q=85"
                        alt="Modern architecture representing professional web design"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Features Grid */}
            <section className="bg-[#F8FAFC] py-16 md:py-24">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
                    Why Businesses Choose Us
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 feature-grid">
                  {[
                    { icon: Zap, title: "Built for Results", desc: "Every website is designed to bring in more calls, leads, and paying customers." },
                    { icon: Clock, title: "Fast Turnaround", desc: "Get your website live in as little as 48 hours." },
                    { icon: Users, title: "Portfolio Pricing", desc: "Discounted rates to build our portfolio — limited spots." },
                    { icon: TrendingUp, title: "Growth Ready", desc: "Mobile-friendly, SEO-ready, built to convert visitors." }
                  ].map((feature, idx) => (
                    <div key={idx} className="bg-white p-6 rounded-lg card-hover">
                      <div className="w-12 h-12 bg-[#FF4500]/10 rounded-lg flex items-center justify-center mb-4">
                        <feature.icon className="w-6 h-6 text-[#FF4500]" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{feature.title}</h3>
                      <p className="text-slate-600 text-sm">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 md:py-24">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className="relative">
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                      <img
                        src="https://images.unsplash.com/photo-1530374260450-4bd19102f63d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHx3ZWIlMjBkZXNpZ24lMjBsYXB0b3AlMjB3b3Jrc3BhY2UlMjBtb2Rlcm58ZW58MHx8fHwxNzc2MjE5OTk4fDA&ixlib=rb-4.1.0&q=85"
                        alt="Web design workspace"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="space-y-6">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
                      Websites Built for <span className="text-[#FF4500]">Any Business</span>
                    </h2>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Whether you're a barber, roofer, plumber, contractor, or any local service business — we build websites that bring in real clients.
                    </p>
                    <ul className="space-y-3">
                      {["Custom design tailored to your industry", "Mobile-optimized & fast loading", "SEO-ready to rank on Google", "Contact forms & lead capture built-in"].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-slate-700">
                          <Check className="w-5 h-5 text-[#FF4500] flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-col sm:flex-row gap-4 pt-2">
                      <button
                        data-testid="home-services-cta"
                        onClick={() => setCurrentPage('services')}
                        className="btn-lift bg-[#FF4500] hover:bg-[#E03C00] text-white px-8 py-4 rounded-lg font-semibold text-lg"
                      >
                        View Our Services
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* SERVICES PAGE */}
        {currentPage === 'services' && (
          <div data-testid="services-page" className="animate-fade-in-up">
            {/* Services Hero */}
            <section className="py-16 md:py-24">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="text-center max-w-3xl mx-auto mb-16">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4500] mb-4">
                    What We Offer
                  </p>
                  <h1 className="text-4xl sm:text-5xl font-black tracking-tighter leading-[1.1] text-[#0F172A]">
                    Everything You Need to <span className="text-[#FF4500]">Grow Online</span>
                  </h1>
                  <p className="text-lg text-slate-600 leading-relaxed mt-6">
                    No matter your industry, we build professional websites that turn visitors into paying customers. One price. No hidden fees.
                  </p>
                </div>

                {/* What's Included Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
                  {[
                    { icon: Monitor, title: "Custom Website Design", desc: "A fully custom website tailored to your business, not a cookie-cutter template. Built to reflect your brand and convert visitors." },
                    { icon: Globe, title: "Mobile-Responsive", desc: "Your site will look and work perfectly on phones, tablets, and desktops. Over 60% of your clients browse on mobile." },
                    { icon: Search, title: "SEO Optimization", desc: "We build your site to rank on Google. Local SEO setup so customers in your area find you first." },
                    { icon: Zap, title: "Fast Load Speed", desc: "Slow websites lose customers. We optimize for speed so visitors stay longer and convert more." },
                    { icon: MessageSquare, title: "Lead Capture Forms", desc: "Built-in contact forms, call-to-action buttons, and click-to-call so leads come straight to you." },
                    { icon: Shield, title: "SSL Security", desc: "Every website comes with SSL encryption. Builds trust with visitors and boosts your Google ranking." }
                  ].map((service, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-xl p-6 card-hover">
                      <div className="w-12 h-12 bg-[#FF4500]/10 rounded-lg flex items-center justify-center mb-4">
                        <service.icon className="w-6 h-6 text-[#FF4500]" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{service.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Who We Work With */}
                <div className="bg-[#F8FAFC] rounded-2xl p-8 md:p-12 mb-20">
                  <div className="text-center mb-10">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
                      Built for Every Business
                    </h2>
                    <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
                      We work with all types of local service businesses. If you need clients, we can build you a website that delivers.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      "Barbers & Salons", "Roofing & Construction", "Plumbers & HVAC", "Electricians",
                      "Auto Detailing", "Landscaping", "Cleaning Services", "Restaurants & Food",
                      "Real Estate Agents", "Fitness & Gyms", "Pet Services", "And More..."
                    ].map((industry, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 border border-slate-200">
                        <Check className="w-4 h-4 text-[#FF4500] flex-shrink-0" />
                        <span className="text-sm font-medium text-[#0F172A]">{industry}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing Section */}
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
                    Simple, Transparent Pricing
                  </h2>
                  <p className="text-slate-600 mt-3">No surprises. No contracts. Just results.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  {/* Website Package */}
                  <div className="relative border-2 border-[#FF4500] rounded-2xl p-8 bg-white">
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-[#FF4500] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full">
                        Most Popular
                      </span>
                    </div>
                    <div className="text-center mb-6 pt-2">
                      <p className="text-sm font-medium text-slate-500 mb-1">One-Time Payment</p>
                      <p className="text-5xl font-black text-[#0F172A]">$350</p>
                      <p className="text-slate-600 text-sm mt-2">Complete custom website</p>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {[
                        "Up to 5 custom pages",
                        "Mobile-responsive design",
                        "Contact forms & lead capture",
                        "Google Maps integration",
                        "Basic SEO setup",
                        "Delivered in 48-72 hours"
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-slate-700 text-sm">
                          <Check className="w-4 h-4 text-[#FF4500] flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <button
                      data-testid="services-checkout-website"
                      onClick={() => checkout('https://buy.stripe.com/dRm6oAd0G6rec475XX6sw00')}
                      className="btn-lift glow-pulse w-full bg-[#FF4500] hover:bg-[#E03C00] text-white py-4 rounded-lg font-semibold text-lg"
                    >
                      Get Your Website
                    </button>
                  </div>

                  {/* Monthly Plan */}
                  <div className="border border-slate-200 rounded-2xl p-8 bg-[#0F172A] text-white">
                    <div className="text-center mb-6">
                      <p className="text-sm font-medium text-slate-300 mb-1">Monthly Plan</p>
                      <p className="text-5xl font-black">$25<span className="text-lg font-normal text-slate-300">/mo</span></p>
                      <p className="text-slate-300 text-sm mt-2">Hosting + Growth Package</p>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {[
                        "Fast & reliable hosting",
                        "SSL security certificate",
                        "Ongoing SEO optimization",
                        "Minor content updates",
                        "Priority email support",
                        "Monthly performance reports"
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-slate-200 text-sm">
                          <Check className="w-4 h-4 text-[#FF4500] flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <button
                      data-testid="services-checkout-monthly"
                      onClick={() => checkout('https://buy.stripe.com/9B67sEbWC2aY2txdqp6sw01')}
                      className="w-full bg-white text-[#0F172A] hover:bg-slate-100 py-4 rounded-lg font-semibold text-lg transition-colors"
                    >
                      Add Monthly Plan
                    </button>
                  </div>
                </div>

                {/* CTA */}
                <div className="text-center mt-16">
                  <p className="text-slate-600 mb-4">Not sure which plan is right for you?</p>
                  <button
                    data-testid="services-contact-cta"
                    onClick={() => setCurrentPage('contact')}
                    className="btn-lift border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
                  >
                    Contact Us for a Free Consultation
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* CONTACT PAGE */}
        {currentPage === 'contact' && (
          <div data-testid="contact-page" className="animate-fade-in-up">
            <section className="py-16 md:py-24">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-2 gap-12">
                  {/* Left Column - Info */}
                  <div className="space-y-8">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4500] mb-4">
                        Get Started Today
                      </p>
                      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter leading-[1.1] text-[#0F172A]">
                        Let's Build Your <span className="text-[#FF4500]">Website</span>
                      </h1>
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Be one of our first clients and lock in discounted pricing before spots fill up. Fill out the form and we'll get back to you within 24 hours.
                    </p>

                    {/* Why Choose Us */}
                    <div className="space-y-4">
                      {[
                        { icon: Zap, title: "Built for Results", desc: "Every website is designed to bring in more calls and paying customers." },
                        { icon: Clock, title: "Fast 48hr Turnaround", desc: "Get your website live quickly so you can start getting clients." },
                        { icon: Star, title: "Portfolio Pricing", desc: "Discounted rates to build our portfolio — limited spots available." },
                        { icon: TrendingUp, title: "Growth Ready", desc: "Mobile-friendly, SEO-ready, built to convert visitors into customers." }
                      ].map((item, idx) => (
                        <div key={idx} className="flex gap-4 p-4 bg-[#F8FAFC] rounded-lg">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-slate-200">
                            <item.icon className="w-5 h-5 text-[#FF4500]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#0F172A]">{item.title}</h3>
                            <p className="text-sm text-slate-600">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Contact Info */}
                    <div className="pt-4 space-y-3">
                      <a href="tel:903-802-2899" className="flex items-center gap-3 text-slate-700 hover:text-[#FF4500] transition-colors">
                        <Phone className="w-5 h-5" />
                        <span className="font-medium">903-802-2899</span>
                      </a>
                      <a href="mailto:Ethandon42@gmail.com" className="flex items-center gap-3 text-slate-700 hover:text-[#FF4500] transition-colors">
                        <Mail className="w-5 h-5" />
                        <span className="font-medium">Ethandon42@gmail.com</span>
                      </a>
                    </div>
                  </div>

                  {/* Right Column - Form */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Get a Free Quote</h2>
                    <form 
                      data-testid="contact-form"
                      action="https://formspree.io/f/xpqkanzl" 
                      method="POST" 
                      className="space-y-4"
                    >
                      <input type="hidden" name="_subject" value="🔥 New Lead - ETX Digital Marketing" />
                      
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                        <input
                          data-testid="contact-name"
                          type="text"
                          id="name"
                          name="name"
                          placeholder="Your name"
                          required
                          className="input-focus w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none transition-all"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                        <input
                          data-testid="contact-phone"
                          type="tel"
                          id="phone"
                          name="phone"
                          placeholder="Your phone number"
                          required
                          className="input-focus w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none transition-all"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                        <input
                          data-testid="contact-email"
                          type="email"
                          id="email"
                          name="email"
                          placeholder="your@email.com"
                          required
                          className="input-focus w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label htmlFor="business" className="block text-sm font-medium text-slate-700 mb-1">Business Type (Optional)</label>
                        <select
                          data-testid="contact-business"
                          id="business"
                          name="business_type"
                          className="input-focus w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none transition-all bg-white"
                        >
                          <option value="">Select your industry</option>
                          <option value="barber">Barber / Barbershop</option>
                          <option value="roofing">Roofing Contractor</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <button
                        data-testid="contact-submit"
                        type="submit"
                        className="btn-lift glow-pulse w-full bg-[#FF4500] hover:bg-[#E03C00] text-white py-4 rounded-lg font-semibold text-lg"
                      >
                        Submit & Get Contacted
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="bg-[#F8FAFC] py-16 md:py-24">
              <div className="max-w-3xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
                    Frequently Asked Questions
                  </h2>
                </div>
                <div className="space-y-3">
                  {faqItems.map((item, idx) => (
                    <div 
                      key={idx}
                      className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                    >
                      <button
                        data-testid={`faq-${idx}`}
                        onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-semibold text-[#0F172A] pr-4">{item.question}</span>
                        {expandedFaq === idx ? (
                          <ChevronUp className="w-5 h-5 text-[#FF4500] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      {expandedFaq === idx && (
                        <div className="px-5 pb-5 text-slate-600">
                          {item.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">E</span>
                </div>
                <span className="font-bold text-xl tracking-tight">ETX Digital</span>
              </div>
              <p className="text-slate-400 text-sm">
                High-converting websites for local businesses. Get more clients, grow your business.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <div className="space-y-2">
                {navigation.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setCurrentPage(item.id)}
                    className="block text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-semibold mb-4">Contact Us</h3>
              <div className="space-y-2 text-slate-400 text-sm">
                <a href="tel:903-802-2899" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                  903-802-2899
                </a>
                <a href="mailto:Ethandon42@gmail.com" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                  Ethandon42@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">© 2026 ETX Digital Marketing. All rights reserved.</p>
            <button 
              data-testid="terms-toggle"
              onClick={() => setTermsOpen(!termsOpen)}
              className="text-slate-400 hover:text-white text-sm underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>

          {/* Terms */}
          {termsOpen && (
            <div data-testid="terms-content" className="mt-6 p-6 bg-slate-900 rounded-lg text-slate-400 text-sm">
              <p>
                By purchasing services from ETX Digital Marketing, you agree that all sales are final.
                We deliver websites within agreed timelines. Monthly plans include hosting and basic updates.
                We are not responsible for third-party outages or platform issues.
              </p>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

export default App;
